<template>
  <div :class="[active.loaded ? '' : 'tw-invisible', { 'tw-dark-layout': isDarkTheme }]"
       class="forgot-page">
    <!-- 內容（教會可用且存在） -->
    <div v-if="active.data.available && active.data.organization.uuid"
         class="tw-flex tw-min-h-screen tw-w-full tw-overflow-hidden tw-bg-white dark:tw-bg-[#161d31]">

      <!-- 品牌 Logo -->
      <a class="tw-absolute tw-left-4 tw-top-4 tw-z-20">
        <img class="tw-h-12 tw-w-12 tw-rounded-md tw-object-cover"
             alt="logo"
             :src="getLogo" />
      </a>

      <!-- 左側：背景圖（大螢幕才顯示） -->
      <div class="tw-relative tw-hidden lg:tw-block lg:tw-flex-1">
        <div class="tw-absolute tw-inset-0 tw-z-[2] tw-bg-white/[0.55] dark:tw-bg-white/[0.25]" />
        <div class="tw-absolute tw-inset-0 tw-z-[1] tw-bg-cover tw-bg-center tw-bg-no-repeat"
             :style="{ backgroundImage: `url(${ getBackground })` }" />
      </div>

      <!-- 右側：表單面板 -->
      <div class="tw-relative tw-flex tw-w-full tw-flex-col tw-justify-center tw-bg-white tw-px-6 tw-py-10 dark:tw-bg-[#343d55] lg:tw-w-[33.333%] lg:tw-min-w-[420px]">
        <div class="tw-mx-auto tw-w-full tw-max-w-md">
          <h2 class="tw-mb-6 tw-text-center tw-text-2xl tw-font-bold tw-text-[#dda026]">
            {{ active.data.name }} - {{ $t('重設登入密碼') }}
          </h2>

          <form @submit.prevent="onSubmit"
                class="tw-flex tw-flex-col tw-gap-4">
            <!-- 別移除, 讓 form 可以用 Enter 自動送出 -->
            <button type="submit" class="tw-hidden" />

            <!-- 新密碼 -->
            <div>
              <label class="f-label">{{ $t('新密碼') }}</label>
              <div class="tw-relative">
                <input v-model="field.create"
                       :type="active.show.create ? 'text' : 'password'"
                       autocomplete="off"
                       :placeholder="$t('請輸入新密碼')"
                       class="f-input tw-pr-11"
                       :class="{ 'f-input--error': errorIn([1001, 1002]) }"
                       @input="active.error = 0" />
                <icon-sax :hover="false"
                          @click="active.show.create = !active.show.create"
                          :type="active.show.create ? 'eye-off' : 'eye'"
                          class="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-cursor-pointer tw-text-[#b9b9c3] ft-20" />
              </div>
              <p v-if="errorIn([1001, 1002])"
                 class="f-error"
                 v-safe-html="$t(`${ configure.prefix }.error.${ active.error }.subject`)" />
            </div>

            <!-- 確認新密碼 -->
            <div>
              <label class="f-label">{{ $t('確認新密碼') }}</label>
              <div class="tw-relative">
                <input v-model="field.confirm"
                       :type="active.show.confirm ? 'text' : 'password'"
                       autocomplete="off"
                       :placeholder="$t('請再輸入一次新密碼進行確認')"
                       class="f-input tw-pr-11"
                       :class="{ 'f-input--error': errorIn([1003, 1004]) }"
                       @input="active.error = 0" />
                <icon-sax :hover="false"
                          @click="active.show.confirm = !active.show.confirm"
                          :type="active.show.confirm ? 'eye-off' : 'eye'"
                          class="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-cursor-pointer tw-text-[#b9b9c3] ft-20" />
              </div>
              <p v-if="errorIn([1003, 1004])"
                 class="f-error"
                 v-safe-html="$t(`${ configure.prefix }.error.${ active.error }.subject`)" />
            </div>

            <!-- 送出 -->
            <button type="button"
                    :disabled="active.xhring || active.expire.value < 1"
                    @click="onSubmit"
                    class="f-btn tw-mt-2"
                    v-safe-html="$t(`submit.${ active.xhring ? 'waiting' : 'forgot.reset' }`)" />
          </form>
        </div>

        <!-- 底部：倒數 / 主題 / 語言 / 返回登入 -->
        <div class="tw-mx-auto tw-mt-8 tw-w-full tw-max-w-md">
          <!-- 令牌倒數 -->
          <div v-if="active.expire.value > 0"
               class="tw-mb-2 tw-text-[11px] tw-text-[#b9b9c3]"
               v-safe-html="sprintf($t('本次重設密碼令牌將在X秒後失效'), active.expire.value)" />
          <div v-else
               class="tw-mb-2 tw-text-[11px] tw-text-[#ea5455] tw-opacity-60"
               v-html="$t('本次重設密碼令牌已經失效')" />

          <div class="tw-flex tw-items-center tw-justify-between">
            <div class="tw-flex tw-items-center tw-gap-2.5">
              <!-- 主題切換 -->
              <el-tooltip :content="$t(isDarkTheme ? '深色主題' : '淺色主題')"
                          placement="top-start">
                <icon-sax @click="setTheme(isDarkTheme ? EnumTheme.LIGHT : EnumTheme.DARK)"
                          class="tw-cursor-pointer ft-20"
                          :class="isDarkTheme ? 'tw-text-[#dda026]' : 'tw-text-[#6e6b7b]'"
                          :type="isDarkTheme ? 'sun' : 'moon'" />
              </el-tooltip>
              <!-- 語言 -->
              <el-dropdown popper-class="light-language"
                           placement="top-start">
                <icon-sax class="tw-cursor-pointer tw-text-[#6e6b7b] ft-20"
                          type="global" />
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item v-for="j in getAllRegion"
                                      :key="j.id"
                                      @click="setRegion(j.id)">
                      <span class="tw-flex tw-items-center">
                        <country-flag :iso="j.id" />
                        <span class="tw-ml-2.5 tw-text-xs"
                              :class="getCurrentRegion.id === j.id ? 'tw-text-[#5e5873]' : 'tw-text-[#b9b9c3]'"
                              v-safe-html="j.value" />
                      </span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
            <!-- 返回登入（放棄重設） -->
            <el-tooltip :content="$t('返回登入')"
                        placement="top-end">
              <icon-sax @click="onDestroy"
                        class="tw-cursor-pointer tw-text-[#6e6b7b] ft-20"
                        type="arrow-right" />
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>

    <!-- 教會不存在 / 不可用 / 令牌失效 -->
    <error404 :home="false"
              v-else />
  </div>
</template>

<script setup>

/**
 * 忘記密碼 - 重設登入密碼
 *
 * 純 Tailwind + JS 重構：雙密碼欄改用原生 input + 顯示切換；倒數與跳轉沿用原頁。
 * API 與 input/output 完全沿用原 /api/forgot/verification/edit/{uuid}/reset（後端已扁平化為 kplug）：
 *   - getEdit 讀 data.data.expire（=0 視為失效），成功 code===200 即重設完成。
 *   - 驗證失敗回 HTTP 200 {code:1001~1004}（強度 1002 由後端判定）；code>=900 時 @/api 攔截器
 *     自動彈出 subject/text，本頁再依 code 標紅對應欄位。
 *
 * 因管理員電子郵件早期可空白、也無唯一值，需以「帳號 + 電子郵件」對應，避免洗錯人。
 */

import IconSax from "@/component/IconSax.vue"
import Error404 from "@/component/Error.vue"
import CountryFlag from "@/component/CountryFlag.vue"

import { computed, onMounted, reactive } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { sprintf } from "sprintf-js"
import { createLogin } from "@/api/module/session"
import http from "@/api"
import { useSetting } from "@/hook/useSetting"
import { usePermission } from "@/hook/usePermission"
import { useRegion } from "@/hook/useRegion"
import { useSwalAlert } from "@/hook/useSwalAlert"
import { EnumTheme } from "@/enum/theme"
import { RouteNameEnum } from "@/v2/enumerate/route"

// API（僅本頁使用 → 內聯實作，不另立 api/module）：重設密碼 /api/forgot/verification/edit/{uuid}/reset/edit/{uid}
// 讀取與作廢皆 builtIn{loadingMask:false, failure:false}（頁面自行處理），送出用預設
const resetUri = (uuid, uid) => `/api/forgot/verification/edit/${uuid}/reset/edit/${uid}`
const silent = { opts: { builtIn: { loadingMask: false, failure: false } } }
const apiResetFetch = (uuid, uid) => http.get(resetUri(uuid, uid), {}, silent)
const apiResetStore = (uuid, uid, params) => http.put(resetUri(uuid, uid), params, {})
const apiResetDestroy = (uuid, uid) => http.delete(resetUri(uuid, uid), {}, silent)

const route = useRoute()
const router = useRouter()

const { t } = useI18n()
const { showWarning, showSuccess, showHandler } = useSwalAlert()
const { isLogin } = usePermission()
const { setTheme, isDarkTheme } = useSetting()
const { setRegion, getAllRegion, getCurrentRegion } = useRegion()

/**
 * 參數 - 頁面 - 配置
 */
const configure = reactive({
  // i18n 代碼前置
  prefix: "page.forgot.verification.reset",
})

/**
 * 參數 - 本地
 */
const active = reactive({
  loaded: false,
  error: 0,
  success: 0,
  xhring: false,
  // 密碼顯示切換
  show: { create: false, confirm: false },
  // 登入頁面資訊
  data: {
    organization: { seq: null, uuid: null, name: null },
    available: true,
    name: null,
    uuid: null,
    avatarUrl: null,
  },
  // 權杖過期倒數
  expire: {
    timer: null,
    value: 0,
  },
})

/**
 * 欄位
 */
const field = reactive({
  create: null,
  confirm: null,
})

/**
 * 錯誤代碼是否落在指定欄位範圍
 */
const errorIn = (codes) => codes.includes(active.error)

/**
 * 銷毀（放棄重設 → 返回登入）
 */
const onDestroy = () => showHandler({
  subject: t("是否確定放棄重設密碼"),
  text: t("確認後將返回登入頁面"),
  completion: {
    success: async () => {
      await apiResetDestroy(route.params.uuid, route.params.uid)
      await router.push({ name: "Login" })
    },
  },
})

/**
 * 提交
 */
const onSubmit = async () => {
  // 效驗 - 新密碼
  if (!field.create) return (active.error = 1001)
  // 效驗 - 新密碼 - 強度 1002（後端會丟下來）
  // 效驗 - 確認密碼
  if (!field.confirm) return (active.error = 1003)
  // 效驗 - 兩次密碼不相符
  if (field.create !== field.confirm) {
    return showWarning({
      subject: t("新密碼確認失敗"),
      text: t("兩次密碼輸入不一致"),
      completion: { done: () => (active.error = 1004) },
    })
  }

  // 標示 - 進行中
  active.xhring = true

  const { data } = await apiResetStore(route.params.uuid, route.params.uid, field)

  // 無法連線 / 伺服器錯誤（攔截器回 false）
  if (data === undefined) {
    return showWarning({
      subject: t("暫時無法使用"),
      text: t("請稍後再試或請聯絡管理人員"),
      completion: { done: () => (active.xhring = false) },
    })
  }

  // 正常（重設成功 → 返回登入）
  if (data.code === 200) {
    return showSuccess({
      subject: t("密碼重設成功"),
      text: t("請使用您設定的新密碼登入"),
      completion: { done: async () => await router.push({ name: "Login" }) },
    })
  }

  // 驗證失敗（攔截器已彈出 subject/text）：標紅對應欄位
  active.error = data.code
  active.xhring = false
}

/**
 * 獲取 Logo
 */
const getLogo = computed(() => {
  if (active.data.organization.seq) return `/img/organization/${ active.data.organization.seq }/logo.png`
  return "/img/organization/1/logo.png"
})

/**
 * 獲取背景圖
 */
const getBackground = computed(() => {
  if (active.data.avatarUrl) return active.data.avatarUrl
  return "/img/avatar/holder/my.jpg"
})

/**
 * 存取 API - 教會 + 重設單資訊
 */
const fetch = async () => {
  const { data } = await createLogin(route.params.id)

  // 返回錯誤不往下進行
  if (data?.data === undefined) return

  active.data.organization = data.data.organization
  active.data.available = data.data.available
  active.data.name = data.data.name
  active.data.uuid = data.data.uuid
  active.data.avatarUrl = data.data.avatarUrl

  // 先存取教會資訊 - 再存取重設資訊
  const { data: e } = await apiResetFetch(route.params.uuid, route.params.uid)

  // 返回錯誤不往下進行或者已超時
  if (e?.data === undefined || e.data.expire === 0) {
    active.data.organization.seq = null
    active.data.organization.uuid = null
    active.data.organization.name = null
    active.data.available = false
    active.loaded = true
    return
  }

  // 登記 - 超時
  active.expire.value = e.data.expire

  // 啟動 - 超時 - 倒數
  active.expire.timer = setInterval(() => {
    active.expire.value -= 1
    if (active.expire.value <= 0) {
      clearInterval(active.expire.timer)
      showWarning({
        subject: t("重設密碼令牌已失效"),
        text: t("請重新進行忘記密碼程序"),
        completion: { done: () => router.replace({ name: "Login" }) },
      })
    }
  }, 1000)

  // 可以打開頁面了
  active.loaded = true
}

/**
 * 生命週期 - 掛載完成
 */
onMounted(() => {
  // 如果已經登入, 前往控制面板
  if (isLogin.value) router.replace({ name: RouteNameEnum.ADMIN_DASHBOARD })
  // 未登入
  else fetch()
})

</script>

<style scoped lang="scss">
// 標籤
.f-label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #5e5873;
}

// 輸入框
.f-input {
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid #d8d6de;
  background: #fff;
  padding: 0.65rem 1rem;
  font-size: 0.9rem;
  color: #5e5873;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #b9b9c3;
  }

  &:focus {
    outline: none;
    border-color: #dda026;
    box-shadow: 0 0 0 3px rgba(221, 160, 38, 0.18);
  }

  &--error {
    border-color: #ea5455;
  }
}

// 錯誤訊息
.f-error {
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: #ea5455;
}

// 送出按鈕（品牌金漸層）
.f-btn {
  width: 100%;
  border: 0;
  border-radius: 0.5rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(118deg, #dda026, rgba(221, 160, 38, 0.7));
  box-shadow: 0 8px 16px -6px rgba(221, 160, 38, 0.5);
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

// 深色模式
:where(.tw-dark-layout) {
  .f-label {
    color: #d0d2d6;
  }

  .f-input {
    border-color: #3b4253;
    background: #283046;
    color: #d0d2d6;

    &::placeholder {
      color: #676d7d;
    }
  }
}
</style>
